import React, { useState, useEffect } from "react";
import { auth, db } from "../../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

import Navbar from "../../components/professor/NavBar";
import Sidebar from "../../components/professor/Sidebar";
import ClassSection from "../../components/professor/ClassSection";
import ExportSection from "../../components/professor/ExportSection";
import InstallSection from "../../components/professor/InstallSection";
import SettingsSection from "../../components/professor/SettingsSection";
import IntegrationSection from "../../components/professor/IntegrationSection";
import Preloader from "../../components/Preloader";
import SupportSection from "../../components/professor/Support";
import DocumentationSection from "../../components/professor/Documentation";
import {
  BookOpen,
  Download,
  FileText,
  HelpCircle,
  Home,
  Link2,
  Settings,
  Wrench,
} from "lucide-react";

const professorTabs = {
  "home-section": {
    eyebrow: "Professor workspace",
    title: "Classes",
    description: "Manage live sessions, students, grades, and class-level activity from one organized workspace.",
    Icon: Home,
  },
  "export-section": {
    eyebrow: "Reports",
    title: "Export",
    description: "Build clean class exports for grades, participation, attendance, and session records.",
    Icon: Download,
  },
  "install-section": {
    eyebrow: "Setup",
    title: "Install",
    description: "Get the classroom tools installed and ready across devices.",
    Icon: Wrench,
  },
  "integration-section": {
    eyebrow: "Connections",
    title: "Integration",
    description: "Connect CoursEngage with the systems you already use for coursework.",
    Icon: Link2,
  },
  "settings-section": {
    eyebrow: "Account",
    title: "Settings",
    description: "Control profile, security, access, and class-level preferences.",
    Icon: Settings,
  },
  "support-section": {
    eyebrow: "Help desk",
    title: "Support",
    description: "Review requests, submit issues, and keep support conversations organized.",
    Icon: HelpCircle,
  },
  "documentation-section": {
    eyebrow: "Guides",
    title: "Documentation",
    description: "Reference implementation notes, setup details, and classroom workflow guidance.",
    Icon: BookOpen,
  },
};

function DashboardTabHeader({ tab }) {
  const Icon = tab.Icon || FileText;

  return (
    <div className="ce-tab-hero">
      <div className="ce-tab-hero-copy">
        <p className="ce-eyebrow">{tab.eyebrow}</p>
        <h1>{tab.title}</h1>
        <p>{tab.description}</p>
      </div>
      <div className="ce-tab-hero-icon" aria-hidden="true">
        <Icon size={24} />
      </div>
    </div>
  );
}

function ProfessorDashboard() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Auth check
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userDocRef = doc(db, "users", currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          if (userData.role === "professor") {
            setUser(currentUser);
          } else {
            window.location.href = "/sign-in";
          }
        } else {
          console.log("No user document found.");
          window.location.href = "/sign-in";
        }
      } else {
        console.log("No user is signed in.");
        window.location.href = "/sign-in";
      }
      setLoading(false);
    });
    return () => unsubscribeAuth();
  }, []);

  // Sign out
  const handleSignOut = async () => {
    try {
      await signOut(auth);
      window.location.href = "/sign-in";
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // Theme handling
  const [theme, setTheme] = useState(() => {
    const storedTheme = localStorage.getItem("theme");
    return storedTheme ? storedTheme : "light";
  });

  useEffect(() => {
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  // Sidebar collapse state (desktop)
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Active section state
  const [activeSection, setActiveSection] = useState("home-section");

  if (loading) {
    return <Preloader />;
  }

  return (
    <div className="ce-dash-shell">
      {/* Top Navbar */}
      <Navbar
        userEmail={user.email}
        onSignOut={handleSignOut}
        theme={theme}
        toggleTheme={toggleTheme}
      />

      {/* Sidebar */}
      <Sidebar
        userEmail={user.email}
        onSignOut={handleSignOut}
        theme={theme}
        toggleTheme={toggleTheme}
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
      />

      {/* Main Content Area */}
      <div
        className={`
          transition-all duration-300 pt-0 lg:pt-16
          ${isCollapsed ? "lg:pl-[72px]" : "lg:pl-64"}
        `}
      >
        <main className="ce-dash-main">
          {activeSection === "home-section" && (
            <section id="home-section" className="ce-tab-section">
              <DashboardTabHeader tab={professorTabs[activeSection]} />
              <ClassSection theme={theme} />
            </section>
          )}
         
          {activeSection === "export-section" && (
            <section id="export-section" className="ce-tab-section">
              <DashboardTabHeader tab={professorTabs[activeSection]} />
              <ExportSection theme={theme} />
            </section>
          )}
          {activeSection === "install-section" && (
            <section id="install-section" className="ce-tab-section">
              <DashboardTabHeader tab={professorTabs[activeSection]} />
              <InstallSection theme={theme} />
            </section>
          )}
          {activeSection === "integration-section" && (
            <section id="integration-section" className="ce-tab-section">
              <DashboardTabHeader tab={professorTabs[activeSection]} />
              <IntegrationSection theme={theme} />
            </section>
          )}
          {activeSection === "settings-section" && (
            <section id="settings-section" className="ce-tab-section">
              <DashboardTabHeader tab={professorTabs[activeSection]} />
              <SettingsSection theme={theme} />
            </section>
          )}
           {activeSection === "support-section" && (
            <section id="support-section" className="ce-tab-section">
              <DashboardTabHeader tab={professorTabs[activeSection]} />
              <SupportSection theme={theme} />
            </section>
          )}
          {activeSection === "documentation-section" && (
            <section id="documentation-section" className="ce-tab-section">
              <DashboardTabHeader tab={professorTabs[activeSection]} />
              {/* <DocumentationSection theme={theme} /> */}
              <section
                id="export-section"
                className="ce-empty"
              >
                <h2>Documentation is being updated.</h2>
                <p className="mt-2">For now, use Support for questions and setup help.</p>
              </section>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}

export default ProfessorDashboard;
